import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  RadioGroup,
  Grid,
  Fade,
  useTheme,
  useMediaQuery,
  styled,
  alpha
} from '@mui/material';
import {
  MessageCircleMore,
  DollarSign,
  HeartPulse,
  Dumbbell,
  Home,
  Wallet
} from 'lucide-react';

// Styles
const FullScreenContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: '#ffffff',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto'
}));

const NavigationBar = styled(Box)(({ theme }) => ({
  padding: '24px 20px',
  textAlign: 'center',
  borderBottom: '1px solid #f0f0f0',
  position: 'sticky',
  top: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  zIndex: 1
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(4, 3),
  overflowY: 'auto',
  paddingBottom: theme.spacing(12), // Añadido espacio adicional para evitar que el contenido quede bajo el botón flotante
}));

// Botón flotante para móviles
const FloatingButtonContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 10,
  width: 'auto',
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(0, 2),
  [theme.breakpoints.up('sm')]: {
    display: 'none', // Solo visible en móviles
  }
}));

// Footer para desktop
const BottomBar = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderTop: '1px solid #f0f0f0',
  position: 'sticky',
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  zIndex: 1,
  display: 'flex',
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    display: 'none', // Oculto en móviles
  }
}));

const AppOptionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3, 2),
  borderRadius: 20,
  border: '2px solid #f0f0f0',
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  cursor: 'pointer',
  backgroundColor: '#ffffff',
  '&:hover': {
    transform: 'translateY(-2px)',
    borderColor: '#e0e0e0',
  },
  '&.selected': {
    borderColor: '#007aff',
    backgroundColor: 'rgba(0, 122, 255, 0.05)',
    transform: 'translateY(-2px)',
  }
}));

const AppIcon = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(1.5),
  background: 'linear-gradient(to bottom, #34aadc, #007aff)',
  color: '#fff',
  [theme.breakpoints.down('sm')]: {
    width: 56,
    height: 56,
  }
}));

const AppName = styled(Typography)(({ theme }) => ({
  fontSize: '17px',
  fontWeight: 600,
  textAlign: 'center',
  color: theme.palette.actualMode === 'wallpaper'
    ? '#FFFFFF'
    : '#1d1d1f',
  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
  letterSpacing: '-0.02em',
  lineHeight: 1.2,
}));

const AppDescription = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 400,
  textAlign: 'center',
  color: theme.palette.actualMode === 'wallpaper'
    ? alpha('#FFFFFF', 0.8)
    : '#86868b',
  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
  marginTop: theme.spacing(0.5),
  lineHeight: 1.3,
}));

// Estilo común para botones
const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '28px',
  padding: '14px 32px',
  backgroundColor: '#007aff',
  '&:hover': {
    backgroundColor: '#0051d5'
  },
  '&:active': {
    backgroundColor: '#004494'
  },
  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
  fontWeight: 600,
  fontSize: '17px',
  minWidth: '160px',
  boxShadow: 'none',
  textTransform: 'none',
  letterSpacing: '-0.01em',
  transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
}));

const SelectApps = ({ open, onClose, onSave }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [selectedApps, setSelectedApps] = useState(['home', 'chat']); // Home and Chat always selected by default
  const [defaultApp, setDefaultApp] = useState('home');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Definition of available applications with descriptions - ACTUALIZADO CON AGT FIN
  const availableApps = [
    { 
      id: 'home', 
      name: t('apps.home.name'), 
      description: t('apps.home.description'),
      icon: Home, 
      required: true 
    },
    { 
      id: 'chat', 
      name: t('apps.chat.name'), 
      description: t('apps.chat.description'),
      icon: MessageCircleMore, 
      required: true 
    },
    { 
      id: 'ups', 
      name: t('apps.ups.name'), 
      description: t('apps.ups.description'),
      icon: DollarSign, 
      required: false 
    },
    { 
      id: 'fin', 
      name: t('apps.fin.name'), 
      description: t('apps.fin.description'),
      icon: Wallet, 
      required: false 
    },
    { 
      id: 'health', 
      name: t('apps.health.name'), 
      description: t('apps.health.description'),
      icon: HeartPulse, 
      required: false 
    },
    { 
      id: 'fit', 
      name: t('apps.fit.name'), 
      description: t('apps.fit.description'),
      icon: Dumbbell, 
      required: false 
    },
  ];

  // Reset state when component opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedApps(['home', 'chat']); // Initial values
      setDefaultApp('home');
    }
  }, [open]);

  // Handle app selection
  const handleAppToggle = (appId) => {
    // If it's a required app, don't allow unselecting it
    const app = availableApps.find(app => app.id === appId);
    if (app?.required) return;

    setSelectedApps(prev => {
      if (prev.includes(appId)) {
        return prev.filter(id => id !== appId);
      } else {
        return [...prev, appId];
      }
    });
  };

  // Handle default app selection
  const handleDefaultAppChange = (appId) => {
    setDefaultApp(appId);
  };

  // Go to next step
  const handleNext = () => {
    setStep(2);
  };

  // Save configuration and close
  const handleSave = () => {
    // NO guardar en localStorage aquí - eso se maneja en el componente padre
    onSave({
      selectedApps,
      defaultApp
    });
  };

  if (!open) return null;

  // Renderizado del botón según el paso actual
  const renderActionButton = () => {
    const buttonProps = {
      variant: "contained",
      onClick: step === 1 ? handleNext : handleSave,
      component: ActionButton
    };
    
    return (
      <ActionButton {...buttonProps}>
        {step === 1 ? t('selectApps.buttons.continue') : t('selectApps.buttons.start')}
      </ActionButton>
    );
  };

  return (
    <FullScreenContainer>
      <NavigationBar>
        <Typography variant="h4" sx={{
          fontSize: '28px',
          fontWeight: 700,
          color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#1d1d1f',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
          marginBottom: '8px'
        }}>
          {step === 1 ? t('selectApps.step1.title') : t('selectApps.step2.title')}
        </Typography>
        <Typography variant="subtitle1" sx={{
          fontSize: '17px',
          color: (theme) => theme.palette.actualMode === 'wallpaper' ? alpha('#FFFFFF', 0.8) : '#86868b',
          fontWeight: 400,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
          lineHeight: 1.4,
          maxWidth: '320px',
          margin: '0 auto'
        }}>
          {step === 1 ? t('selectApps.step1.subtitle') : t('selectApps.step2.subtitle')}
        </Typography>
      </NavigationBar>

      <ContentArea>
        {step === 1 ? (
          <Fade in={step === 1}>
            <Grid container spacing={3}>
              {availableApps.map((app) => (
                <Grid item xs={6} sm={4} md={3} key={app.id}>
                  <AppOptionContainer
                    className={selectedApps.includes(app.id) ? 'selected' : ''}
                    onClick={() => handleAppToggle(app.id)}
                  >
                    <AppIcon>
                      <app.icon
                        size={isMobile ? 24 : 28}
                        strokeWidth={2.5}
                        color={theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#FFFFFF'}
                      />
                    </AppIcon>
                    <AppName>{app.name}</AppName>
                    <AppDescription>{app.description}</AppDescription>
                  </AppOptionContainer>
                </Grid>
              ))}
            </Grid>
          </Fade>
        ) : (
          <Fade in={step === 2}>
            <Box>
              <RadioGroup
                value={defaultApp}
                onChange={(e) => handleDefaultAppChange(e.target.value)}
              >
                <Grid container spacing={3}>
                  {selectedApps.map((appId) => {
                    const app = availableApps.find(a => a.id === appId);
                    if (!app) return null;
                    
                    return (
                      <Grid item xs={6} sm={4} md={3} key={app.id}>
                        <AppOptionContainer
                          className={defaultApp === app.id ? 'selected' : ''}
                          onClick={() => handleDefaultAppChange(app.id)}
                        >
                          <AppIcon>
                            <app.icon
                              size={isMobile ? 24 : 28}
                              strokeWidth={2.5}
                              color={theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#FFFFFF'}
                            />
                          </AppIcon>
                          <AppName>{app.name}</AppName>
                          <AppDescription>{app.description}</AppDescription>
                        </AppOptionContainer>
                      </Grid>
                    );
                  })}
                </Grid>
              </RadioGroup>

              <Typography variant="caption" sx={{
                display: 'block',
                mt: 5,
                textAlign: 'center',
                color: (theme) => theme.palette.actualMode === 'wallpaper' ? alpha('#FFFFFF', 0.7) : '#86868b',
                fontSize: '15px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
                lineHeight: 1.4
              }}>
                {t('selectApps.settingsNote')}
              </Typography>
            </Box>
          </Fade>
        )}
      </ContentArea>

      {/* Botón flotante para móviles */}
      <FloatingButtonContainer>
        {renderActionButton()}
      </FloatingButtonContainer>

      {/* Footer para desktop */}
      <BottomBar>
        {renderActionButton()}
      </BottomBar>
    </FullScreenContainer>
  );
};

export default SelectApps;